import React, { useMemo, useState } from "react";
import {
  Card,
  Tag,
  Table,
  Button,
  Space,
  TableColumnsType,
  Spin,
  Empty,
  Modal,
  Tabs,
} from "antd";
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CarOutlined,
  EyeOutlined,
  CalendarOutlined,
  TruckOutlined,
  BarsOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDistpachStore } from "./dispatch.store";
import { useGetServiceTickets } from "~/services/service-ticket";
import { useGetTasksForDate } from "~/services/task";
import { TInventory } from "~/types";
import { TStaff } from "~/services/operation";
import { Task } from "~/types/task";
import { ServiceType, TaskStatus, Priority } from "~/utils/constants";
import {
  capitalize,
  formatDimensions,
  formatFullAddress,
  getClientName,
} from "~/utils";
import { TableInventory } from "../../inventories/list-inventories/table-inventory";
import { TWaybillService } from "~/types/waybill-service";
import { PrintWaybill } from "../print-labels/print-waybill";
import { PrintLabels } from "../print-labels/print-label";
import { useCompleteTask as useCompleteTaskStore } from "~/components/common/complete-task/complete-task.store";
import { CompleteTaskModal } from "~/components/common/complete-task/complete-task-modal";
import { ContainerInfoCard } from "~/components/common/container-info/container-info-card";
import { DispatchNoteModal } from "./dispatch-note-modal";
import { TaskType } from "~/utils/constants";

interface DailyOverviewProps {
  selectedDate: string;
  compactView: boolean;
}

export const DailyOverview: React.FC<DailyOverviewProps> = ({
  compactView,
}) => {
  const { filters } = useDistpachStore();
  const { openCompleteTask } = useCompleteTaskStore();
  const [activeTab, setActiveTab] = useState<string>("timeline");
  const [selectedService, setSelectedService] = useState<any>(null);
  const [dispatchNoteOpen, setDispatchNoteOpen] = useState(false);

  const formattedFilters = useMemo(() => {
    const _filters: Record<string, any> = {
      pageSize: 200,
    };
    if (filters.startDate) {
      const startOfDate = dayjs(filters.startDate).startOf("day").toISOString();
      const endOfDate = dayjs(filters.startDate).endOf("day").toISOString();
      _filters.startDate = startOfDate;
      _filters.endDate = endOfDate;
    }
    return _filters;
  }, [filters]);

  const { data: serviceTickets, isLoading } =
    useGetServiceTickets(formattedFilters);

  // Use date-only strings for tasks query to avoid timezone-related duplication
  const taskDateFilter = useMemo(() => {
    if (!filters.startDate) return { start: "", end: "" };
    const dateStr = dayjs(filters.startDate).format("YYYY-MM-DD");
    return { start: dateStr, end: dateStr };
  }, [filters.startDate]);

  const { data: tasksForDate, isLoading: tasksLoading } = useGetTasksForDate(
    taskDateFilter.start,
    taskDateFilter.end,
    !!taskDateFilter.start
  );

  // Unique staff working this day (from service tickets + tasks)
  const workingStaff = useMemo(() => {
    const staffMap = new Map<string, TStaff>();

    // Staff from service tickets
    serviceTickets?.items?.forEach((ticket) => {
      ticket.staffs?.forEach((staff: TStaff) => {
        if (staff.id && !staffMap.has(staff.id)) {
          staffMap.set(staff.id, staff);
        }
      });
    });

    // Staff from tasks (assignees)
    tasksForDate?.forEach((task: Task) => {
      task.assignees?.forEach((staff) => {
        if (staff.id && !staffMap.has(staff.id)) {
          staffMap.set(staff.id, staff as TStaff);
        }
      });
    });

    return Array.from(staffMap.values());
  }, [serviceTickets, tasksForDate]);

  const getTimelinePosition = (service: TWaybillService): number => {
    const start = dayjs(service.startDatetime);
    const hour = start.hour();
    const minutes = start.minute();
    const totalHours = hour + minutes / 60;
    return (totalHours * 100) / 24;
  };

  const getTimelineWidth = (service: TWaybillService): number => {
    const start = dayjs(service.startDatetime);
    const end = dayjs(service.startDatetime).add(service.duration ?? 0, "hour");
    const startHour = start.hour() + start.minute() / 60;
    const endHour = end.hour() + end.minute() / 60;
    const duration = endHour - startHour;
    return (duration * 100) / 24;
  };

  const inventoryColumns: TableColumnsType<TInventory> = [
    {
      title: "ID",
      dataIndex: "inventoryRef",
      key: "id",
      className: "text-gray-500",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      className: "text-gray-500",
    },
    {
      title: "Dimensions",
      key: "dimensions",
      className: "text-gray-500",
      render: (_, record: TInventory) => formatDimensions(record),
    },
    {
      title: "Special Handling",
      dataIndex: "specialHandling",
      key: "specialHandling",
      render: (_, record) => {
        return (
          <div className="flex flex-wrap gap-1">
            {record.specialHandling?.map((service) => (
              <Tag key={service} color="blue" className="text-xs">
                {capitalize(service)}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ];

  const waybillServices: TWaybillService[] = useMemo(() => {
    return (
      serviceTickets?.items.map((ticket) => ({
        ...ticket,
        clientService: ticket.client,
        booking: ticket.jobTicket,
      })) || []
    ).sort((a, b) => {
      // Sort by time (earliest first)
      const timeA = dayjs(a.startDatetime).valueOf();
      const timeB = dayjs(b.startDatetime).valueOf();

      if (timeA !== timeB) {
        return timeA - timeB; // Earlier time first
      }

      // If times are equal, sort by vessel name
      const vesselNameA = a.vessel?.name || "";
      const vesselNameB = b.vessel?.name || "";
      return vesselNameA.localeCompare(vesselNameB);
    });
  }, [serviceTickets]);

  // Time slots for timeline view (7am to 7pm)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 19; hour++) {
      const time = dayjs().hour(hour).minute(0).second(0);
      slots.push({
        hour,
        label: time.format("h A"),
        fullLabel: time.format("h:mm A")
      });
    }
    return slots;
  }, []);

  // Group services by vessel and special categories for timeline view
  const servicesByVessel = useMemo(() => {
    const groups: Record<string, TWaybillService[]> = {
      warehouse: [],
      fabrication: [],
      site: [],
      unassigned: [],
    };

    // Group for regular vessels
    const vesselGroups: Record<string, TWaybillService[]> = {};

    waybillServices.forEach((service) => {
      // Use ONLY the serviceType field (authoritative source) for categorization
      const serviceType = service.serviceType?.toLowerCase() || "";

      // Check for special categories based on service type only
      if (
        serviceType === "warehouse_service" ||
        serviceType.includes("warehouse")
      ) {
        groups["warehouse"].push(service);
        return;
      }

      if (
        serviceType === "fabrication_service" ||
        serviceType.includes("fabrication")
      ) {
        groups["fabrication"].push(service);
        return;
      }

      if (
        serviceType === "on_site_service" ||
        serviceType.includes("site")
      ) {
        groups["site"].push(service);
        return;
      }

      // Group by vessel
      const vesselId = service.vessel?.id || "unassigned";
      const vesselName = service.vessel?.name || "Unassigned";

      if (!vesselGroups[vesselName]) {
        vesselGroups[vesselName] = [];
      }
      vesselGroups[vesselName].push(service);
    });

    // Sort services in each group by time
    const sortByTime = (services: TWaybillService[]) => {
      return services.sort((a, b) => {
        return dayjs(a.startDatetime).valueOf() - dayjs(b.startDatetime).valueOf();
      });
    };

    // Apply sorting to all groups
    Object.keys(groups).forEach(key => {
      groups[key] = sortByTime(groups[key]);
    });
    Object.keys(vesselGroups).forEach(key => {
      vesselGroups[key] = sortByTime(vesselGroups[key]);
    });

    return { ...groups, ...vesselGroups };
  }, [waybillServices]);

  // Timeline component for vessel view
  const TimelineView = () => {
    const getServicePosition = (service: TWaybillService) => {
      const start = dayjs(service.startDatetime);
      const hour = start.hour();
      const minutes = start.minute();
      const totalMinutes = hour * 60 + minutes;
      const startMinutes = 7 * 60; // 7 AM
      const endMinutes = 19 * 60; // 7 PM
      const totalRange = endMinutes - startMinutes;
      return ((totalMinutes - startMinutes) / totalRange) * 100;
    };

    const getServiceWidth = (service: TWaybillService) => {
      const duration = service.duration || 1;
      const totalRange = (19 - 7) * 60; // 12 hours in minutes
      return (duration * 60 / totalRange) * 100;
    };

    return (
      <div className="space-y-6">
        {/* Timeline Header */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <CalendarOutlined className="text-blue-600" />
              <span className="font-semibold text-lg">Vessel Timeline</span>
            </div>
            <div className="text-sm text-gray-600">
              {dayjs(filters.startDate).format("dddd, MMMM D, YYYY")}
            </div>
          </div>
          
          {/* Time scale */}
          <div className="relative h-12 bg-white rounded border mb-4">
            <div className="absolute inset-0 flex justify-between items-center px-2">
              {timeSlots.map((slot, index) => (
                <div key={slot.hour} className="flex flex-col items-center">
                  <div className="text-xs text-gray-500 font-medium">
                    {slot.label}
                  </div>
                  <div className="w-px h-2 bg-gray-300 mt-1"></div>
                </div>
              ))}
            </div>
            {/* Hour grid lines */}
            <div className="absolute inset-0 flex justify-between">
              {timeSlots.map((slot, index) => (
                <div 
                  key={slot.hour} 
                  className="w-px h-full bg-gray-200"
                  style={{ marginLeft: index === 0 ? '0' : '-1px' }}
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Dispatch Notes Section */}
        {tasksForDate && tasksForDate.filter((t: Task) => t.taskType === TaskType.DISPATCH_NOTE).length > 0 && (
          <div className="bg-white rounded-lg border shadow-sm border-l-4 border-l-indigo-500">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 mr-2">
                    <FileTextOutlined />
                  </span>
                  <span className="font-semibold text-lg">Dispatch Notes</span>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {tasksForDate
                .filter((t: Task) => t.taskType === TaskType.DISPATCH_NOTE)
                .map((task: Task) => (
                  <div
                    key={task.id}
                    className="bg-indigo-50 border border-indigo-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 mt-0.5">
                        <FileTextOutlined />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {task.title}
                          </span>
                          <span className="text-xs text-gray-400">
                            {task.taskRef}
                          </span>
                          <Tag color="purple" className="text-xs">
                            Pinned 7:30 AM
                          </Tag>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                            {task.description}
                          </p>
                        )}
                        {task.assignees && task.assignees.length > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                              <UserOutlined />
                              <span>Sent to {task.assignees.length} staff:</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {task.assignees.map((a) => (
                                <Tag key={a.id} className="text-xs">
                                  {a.user
                                    ? `${a.user.firstName || ""} ${a.user.lastName || ""}`.trim()
                                    : "Staff"}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Internal Tasks Section */}
        {tasksForDate && tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).length > 0 && (
          <div className="bg-white rounded-lg border shadow-sm border-l-4 border-l-amber-400">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600 mr-2">
                    <ClockCircleOutlined />
                  </span>
                  <span className="font-semibold text-lg">Internal Tasks</span>
                  <span className="text-sm text-gray-500 ml-2">
                    ({tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).length} task{tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).length !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).map((task: Task) => {
                const getPriorityColor = (p: Priority) => {
                  switch (p) {
                    case Priority.HIGH: return "red";
                    case Priority.MEDIUM: return "orange";
                    case Priority.LOW: return "green";
                    default: return "default";
                  }
                };
                const getTaskStatusColor = (s: TaskStatus) => {
                  switch (s) {
                    case TaskStatus.COMPLETED: return "green";
                    case TaskStatus.IN_PROGRESS: return "blue";
                    case TaskStatus.BLOCKED: return "red";
                    default: return "default";
                  }
                };

                return (
                  <div
                    key={task.id}
                    className="bg-amber-50 border border-amber-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {task.title}
                          </span>
                          <span className="text-xs text-gray-400">
                            {task.taskRef}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                            {task.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {task.taskStatus !== TaskStatus.COMPLETED && (
                          <button
                            className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                            onClick={() => openCompleteTask(task)}
                          >
                            Complete
                          </button>
                        )}
                        <Tag color={getPriorityColor(task.priority)}>
                          {capitalize(task.priority)}
                        </Tag>
                        <Tag color={getTaskStatusColor(task.taskStatus)}>
                          {capitalize(task.taskStatus.replace("_", " "))}
                        </Tag>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {task.startTime && (
                        <span className="flex items-center gap-1">
                          <ClockCircleOutlined />
                          {dayjs(`2025-01-01 ${task.startTime}`).format("h:mm A")}
                          {task.endTime &&
                            ` - ${dayjs(`2025-01-01 ${task.endTime}`).format("h:mm A")}`}
                        </span>
                      )}
                      {task.duration && (
                        <span>{task.duration}h</span>
                      )}
                      {task.assignees && task.assignees.length > 0 && (
                        <span className="flex items-center gap-1">
                          <UserOutlined />
                          {task.assignees
                            .map((a) =>
                              a.user
                                ? `${a.user.firstName || ""} ${a.user.lastName || ""}`.trim()
                                : "Staff"
                            )
                            .join(", ")}
                        </span>
                      )}
                      {task.jobTicket && (
                        <span className="flex items-center gap-1">
                          Booking: {task.jobTicket.referenceNumber}
                        </span>
                      )}
                      {task.dueDate && (
                        <span>
                          Due: {dayjs(task.dueDate).format("MMM D")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {Object.entries(servicesByVessel).map(([groupName, services]) => {
          if (services.length === 0) return null;

          const getGroupIcon = (name: string) => {
            switch (name) {
              case 'warehouse':
                return <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z" />
                    <path d="M6 18h12" />
                    <path d="M6 14h12" />
                    <rect x="6" y="10" width="12" height="12" />
                  </svg>
                </span>;
              case 'fabrication':
                return <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600 mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                  </svg>
                </span>;
              case 'site':
                return <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 7.12h-6l2-5.73A8 8 0 0 0 12 1.73 8 8 0 0 0 9 15.88a5.5 5.5 0 0 1 8 0A8 8 0 0 0 20 9" />
                    <path d="M12 12.88V17" />
                    <path d="M8 21.88a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4H8Z" />
                  </svg>
                </span>;
              default:
                return <TruckOutlined className="text-gray-500 mr-2" />;
            }
          };

          return (
            <div key={groupName} className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getGroupIcon(groupName)}
                    <span className="font-semibold text-lg capitalize">{groupName}</span>
                    <span className="text-sm text-gray-500 ml-2">
                      ({services.length} service{services.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4">
                {/* Timeline for this group */}
                <div className="relative h-20 bg-gray-50 rounded-lg border mb-4">
                  {/* Hour grid lines for this timeline */}
                  <div className="absolute inset-0 flex justify-between">
                    {timeSlots.map((slot, index) => (
                      <div 
                        key={slot.hour} 
                        className="w-px h-full bg-gray-200"
                        style={{ marginLeft: index === 0 ? '0' : '-1px' }}
                      ></div>
                    ))}
                  </div>
                  
                  {services.map((service, index) => {
                    const position = getServicePosition(service);
                    const width = getServiceWidth(service);

                    // Get timeline bar color based on group type and vessel color
                    const getTimelineBarColor = () => {
                      // For warehouse and fabrication groups, use purple/pink
                      if (groupName === 'warehouse' || groupName === 'fabrication') {
                        return { backgroundColor: '#c4b5fd' }; // purple-300
                      }

                      // For site group, use gray
                      if (groupName === 'site') {
                        return { backgroundColor: '#9ca3af' }; // gray-400
                      }

                      // For vessel groups, use vessel color
                      const vesselColor = service.vessel?.color;
                      if (vesselColor) {
                        const vesselColorMap: Record<string, string> = {
                          'tomato': '#fca5a5',      // red-300
                          'flamingo': '#f9a8d4',    // pink-300
                          'tangerine': '#fdba74',   // orange-300
                          'banana': '#fde047',      // yellow-300
                          'sage': '#86efac',        // green-300
                          'basil': '#6ee7b7',       // emerald-300
                          'peacock': '#67e8f9',     // cyan-300
                          'blueberry': '#93c5fd',   // blue-300
                          'lavender': '#c4b5fd',    // purple-300
                          'grape': '#d8b4fe',       // purple-300
                          'graphite': '#9ca3af',    // gray-400
                        };
                        return { backgroundColor: vesselColorMap[vesselColor] || '#93c5fd' };
                      }

                      // Default for unassigned
                      return { backgroundColor: '#9ca3af' }; // gray-400
                    };

                    // Get service info
                    const refNumber = service.serial;
                    const clientName = service.client?.companyName || 'No Client';
                    const bookingClient = service.jobTicket?.client?.companyName;
                    const duration = service.duration || 1;
                    const timeText = dayjs(service.startDatetime).format("h:mm A");

                    return (
                      <div
                        key={service.id}
                        className="absolute top-1 bottom-1 rounded border-2 border-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                        style={{
                          ...getTimelineBarColor(),
                          left: `${Math.max(0, position)}%`,
                          width: `${Math.min(width, 100 - position)}%`,
                        }}
                        onClick={() => setSelectedService(service)}
                        title={`${refNumber} - ${clientName} - ${timeText} (${duration}h)`}
                      >
                        <div className="h-full flex flex-col justify-center text-gray-800 text-xs font-medium px-2 py-1">
                          <div className="font-bold truncate">#{refNumber}</div>
                          <div className="truncate">{clientName}</div>
                          {bookingClient && bookingClient !== clientName && (
                            <div className="truncate text-gray-600 text-xs">({bookingClient})</div>
                          )}
                          <div className="text-xs text-gray-600">{duration}h</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Service cards for this group - using schedule view style */}
                <div className="space-y-3">
                  {services.map((service) => (
                    <ContainerInfoCard
                      key={service.id}
                      serviceTicket={service}
                      variant="simpleschedule"
                      showHeader={false}
                      className="hover:shadow-md transition-shadow"
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // List view component
  const ListView = () => (
    <div className="space-y-4">
      {waybillServices.length > 0 ? (
        waybillServices.map((service) => (
          <ContainerInfoCard
            key={service.id}
            serviceTicket={service}
            variant="simpleschedule"
            showHeader={false}
            className="hover:shadow-md transition-shadow"
          />
        ))
      ) : (
        <Empty description="No services scheduled for this date" />
      )}
    </div>
  );

  return (
    <Spin spinning={isLoading || tasksLoading} size="large">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <CalendarOutlined className="text-blue-600 text-2xl" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {dayjs(filters.startDate).format("dddd, MMMM D, YYYY")}
                </h2>
                <p className="text-sm text-gray-600">
                  {waybillServices.length} service{waybillServices.length !== 1 ? 's' : ''}
                  {tasksForDate && tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).length > 0 && (
                    <>, {tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).length} task{tasksForDate.filter((t: Task) => t.taskType !== TaskType.DISPATCH_NOTE).length !== 1 ? 's' : ''}</>
                  )}
                  {tasksForDate && tasksForDate.filter((t: Task) => t.taskType === TaskType.DISPATCH_NOTE).length > 0 && (
                    <>, {tasksForDate.filter((t: Task) => t.taskType === TaskType.DISPATCH_NOTE).length} dispatch note{tasksForDate.filter((t: Task) => t.taskType === TaskType.DISPATCH_NOTE).length !== 1 ? 's' : ''}</>
                  )}
                  {' '}scheduled
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Tag color="blue" className="rounded-full">
                Dispatch Summary
              </Tag>
              <Space>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => setDispatchNoteOpen(true)}
                  className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                >
                  Dispatch Note
                </Button>
                <PrintWaybill
                  selectedServiceTicketsForPrint={waybillServices}
                  all
                />
                <PrintLabels
                  selectedInventories={waybillServices.flatMap(
                    (service) => service.inventories || []
                  )}
                  all
                />
              </Space>
            </div>
          </div>

          {/* View Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "timeline",
                label: (
                  <span className="flex items-center space-x-2">
                    <CalendarOutlined />
                    <span>Timeline View</span>
                  </span>
                ),
              },
              {
                key: "list",
                label: (
                  <span className="flex items-center space-x-2">
                    <BarsOutlined />
                    <span>List View</span>
                  </span>
                ),
              },
            ]}
          />
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          {activeTab === "timeline" ? <TimelineView /> : <ListView />}
        </div>

        {/* Service Detail Modal */}
        <Modal
          title="Service Details"
          open={!!selectedService}
          onCancel={() => setSelectedService(null)}
          footer={null}
          width={900}
        >
          {selectedService && (
            <ContainerInfoCard
              serviceTicket={selectedService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          )}
        </Modal>

        {/* Task Completion Modal */}
        <CompleteTaskModal />

        {/* Dispatch Note Modal */}
        <DispatchNoteModal
          open={dispatchNoteOpen}
          onClose={() => setDispatchNoteOpen(false)}
          date={filters.startDate}
          workingStaff={workingStaff}
        />
      </div>
    </Spin>
  );
};

const ViewDetailInventories = ({
  inventories,
}: {
  inventories: TInventory[];
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        icon={<EyeOutlined />}
        size={"large"}
        className="border rounded-lg hover:bg-gray-50 bg-white shadow-sm"
        onClick={() => setIsModalOpen(true)}
      >
        View Details
      </Button>
      <Modal
        title="Inventory Details"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={"70%"}
        footer={false}
      >
        <TableInventory inventories={inventories} pagination={{}} />
      </Modal>
    </>
  );
};
